import { useEffect, useRef, useState } from "react";
import {
  BadgeCheck,
  BellRing,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  FileDown,
  FileText,
  Handshake,
  History,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Calendar } from "../../components/ui/calendar";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Loading, PageHeader, StatCard } from "../../components/shared";

const emptyContract = {
  title: "",
  contract_type: "",
  provider_id: "",
  start_date: "",
  end_date: "",
  contract_value: "",
  tax_amount: "",
  billing_frequency: "ANNUAL",
  service_frequency: "CUSTOM",
  coverage_type: "SERVICE",
  covered_service: "",
  planned_visits: "",
  next_service_date: "",
  renewal_notice_days: 30,
  payment_terms: "",
  terms_conditions: "",
  remarks: "",
  asset_ids: [],
  covered_locations: [],
};

const schedules = [
  ["dashboard", "Dashboard"],
  ["contracts", "Contracts"],
  ["schedule", "Service Schedule"],
  ["visits", "Service Visits"],
  ["providers", "Providers"],
  ["reports", "Reports"],
  ["settings", "Settings"],
];

const frequencyOptions = [
  "MONTHLY",
  "QUARTERLY",
  "HALF_YEARLY",
  "ANNUAL",
  "ONE_TIME",
  "CUSTOM",
];

function Field({ children, label }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function DatePicker({ label, onChange, testid, value }) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Field label={label}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            className="w-full justify-start font-normal"
            data-testid={testid}
            type="button"
            variant="outline"
          >
            <CalendarDays className="mr-2 h-4 w-4" />
            {selected ? selected.toLocaleDateString("en-CA") : "Select date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            initialFocus
            mode="single"
            onSelect={(next) =>
              onChange(next ? next.toISOString().slice(0, 10) : "")
            }
            selected={selected}
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
}

function StateBadge({ value }) {
  const color = {
    ACTIVE: "bg-emerald-100 text-emerald-800",
    DRAFT: "bg-slate-100 text-slate-700",
    EXPIRING_SOON: "bg-amber-100 text-amber-800",
    EXPIRED: "bg-rose-100 text-rose-800",
    RENEWED: "bg-blue-100 text-blue-800",
    COMPLETED: "bg-emerald-100 text-emerald-800",
    OVERDUE: "bg-rose-100 text-rose-800",
    DUE: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ${
        color[value] || "bg-muted text-muted-foreground"
      }`}
      data-testid={`amc-state-${String(value).toLowerCase()}`}
    >
      {String(value || "UNKNOWN").replaceAll("_", " ")}
    </span>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function tableEmpty(message, testid, columns) {
  return (
    <tr data-testid={testid}>
      <td
        className="p-10 text-center text-sm text-muted-foreground"
        colSpan={columns}
      >
        {message}
      </td>
    </tr>
  );
}

export default function SocietyAmc() {
  const [bootstrap, setBootstrap] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [allSchedules, setAllSchedules] = useState(null);
  const [visits, setVisits] = useState(null);
  const [providers, setProviders] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    expiry_days: "",
  });
  const [contractDialog, setContractDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState("");

  const load = async () => {
    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== ""),
      );
      const [boot, dash, contractRows, scheduleRows, visitRows, providerRows] =
        await Promise.all([
          api.get("/society-amc/bootstrap"),
          api.get("/society-amc/dashboard"),
          api.get(`/society-amc/contracts?${query.toString()}`),
          api.get("/society-amc/schedules"),
          api.get("/society-amc/visits"),
          api.get("/society-amc/providers"),
        ]);
      setBootstrap(boot.data);
      setDashboard(dash.data);
      setContracts(contractRows.data);
      setAllSchedules(scheduleRows.data);
      setVisits(visitRows.data);
      setProviders(providerRows.data);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load AMC & Contracts",
      );
    }
  };

  useEffect(() => {
    load();
  }, [filters.q, filters.status, filters.expiry_days]);

  if (
    !bootstrap ||
    !dashboard ||
    !contracts ||
    !allSchedules ||
    !visits ||
    !providers
  ) {
    return <Loading />;
  }

  const applyFilter = (next) => {
    setFilters({ ...filters, ...next });
    setActiveTab(next.status || next.expiry_days ? "contracts" : "schedule");
  };

  return (
    <div data-testid="society-amc-page">
      <PageHeader
        title="AMC & Contracts"
        subtitle={
          "Control service coverage, provider commitments, visits, and renewals without " +
          "changing financial records."
        }
      >
        <Button
          data-testid="amc-refresh-button"
          onClick={load}
          variant="outline"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Refresh
        </Button>
        <Button
          data-testid="amc-create-contract-button"
          onClick={() => setContractDialog(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          New Contract
        </Button>
      </PageHeader>
      <Tabs onValueChange={setActiveTab} value={activeTab}>
        <TabsList
          className="mb-6 h-auto flex-wrap"
          data-testid="amc-navigation-tabs"
        >
          {schedules.map(([value, label]) => (
            <TabsTrigger
              data-testid={`amc-tab-${value}`}
              key={value}
              value={value}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dashboard">
          <AmcDashboard dashboard={dashboard} onFilter={applyFilter} />
        </TabsContent>
        <TabsContent value="contracts">
          <ContractRegister
            contracts={contracts}
            filters={filters}
            onFilters={setFilters}
            onOpen={setSelectedContract}
          />
        </TabsContent>
        <TabsContent value="schedule">
          <ScheduleRegister
            schedules={allSchedules}
            onOpen={setSelectedContract}
          />
        </TabsContent>
        <TabsContent value="visits">
          <VisitRegister visits={visits} />
        </TabsContent>
        <TabsContent value="providers">
          <ProviderRegister
            bootstrap={bootstrap}
            onChanged={load}
            providers={providers}
          />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsPanel />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsPanel bootstrap={bootstrap} onChanged={load} />
        </TabsContent>
      </Tabs>
      <ContractEditor
        bootstrap={bootstrap}
        onChanged={load}
        onOpenChange={setContractDialog}
        open={contractDialog}
      />
      <Contract360
        bootstrap={bootstrap}
        contractId={selectedContract}
        onChanged={load}
        onClose={() => setSelectedContract("")}
      />
    </div>
  );
}

function AmcDashboard({ dashboard, onFilter }) {
  const cards = [
    [
      "active-contracts",
      "Active Contracts",
      dashboard.active_contracts,
      Handshake,
      "ACTIVE",
    ],
    [
      "expiring-30",
      "Expiring in 30 days",
      dashboard.expiring_30,
      BellRing,
      "EXPIRING_SOON",
    ],
    [
      "expiring-60",
      "Expiring in 60 days",
      dashboard.expiring_60,
      CalendarClock,
      "EXPIRING_SOON",
    ],
    [
      "expired",
      "Expired Contracts",
      dashboard.expired_contracts,
      ShieldAlert,
      "EXPIRED",
    ],
    ["renewal", "Renewal Due", dashboard.renewal_due, History, "EXPIRING_SOON"],
    ["due", "Services Due", dashboard.services_due, ClipboardCheck, "due"],
    [
      "overdue",
      "Overdue Services",
      dashboard.overdue_services,
      ShieldAlert,
      "overdue",
    ],
    [
      "completed",
      "Completed Services",
      dashboard.completed_services,
      CheckCircle2,
      "completed",
    ],
  ];
  return (
    <div>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(([key, label, value, Icon, filter]) => (
          <button
            className={
              "rounded-xl border bg-card p-5 text-left shadow-sm transition-transform " +
              "hover:-translate-y-0.5 hover:shadow-md"
            }
            data-testid={`amc-kpi-${key}`}
            key={key}
            onClick={() =>
              onFilter(
                ["due", "overdue", "completed"].includes(filter)
                  ? { status: "", expiry_days: "" }
                  : {
                      status: filter,
                      expiry_days: filter === "EXPIRING_SOON" ? "60" : "",
                    },
              )
            }
          >
            <Icon className="h-5 w-5 text-primary" />
            <div
              className="mt-4 font-display text-3xl font-bold"
              data-testid={`amc-kpi-value-${key}`}
            >
              {value}
            </div>
            <div className="text-sm text-muted-foreground">{label}</div>
          </button>
        ))}
      </section>
      <section
        className="mt-6 border bg-card p-5"
        data-testid="amc-reminders-panel"
      >
        <div className="flex items-start gap-3">
          <BellRing className="mt-0.5 h-5 w-5 text-amber-700" />
          <div>
            <h2 className="font-display font-bold">Renewal alerts</h2>
            <p className="text-sm text-muted-foreground">
              In-app reminders are calculated at 90, 60, 30, 15, 7, and
              expiry-day checkpoints. Existing email reminders continue only
              where the current email service is configured.
            </p>
          </div>
        </div>
        {dashboard.reminders.length === 0 ? (
          <p
            className="mt-4 text-sm text-muted-foreground"
            data-testid="amc-reminders-empty"
          >
            No contract is at a configured reminder checkpoint today.
          </p>
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {dashboard.reminders.map((contract) => (
              <div
                className="border-l-2 border-amber-400 bg-amber-50 p-3"
                key={contract.contract_id}
              >
                <div className="font-medium">
                  {contract.contract_number} · {contract.title}
                </div>
                <div className="text-sm text-amber-900">
                  {contract.expiry_days === 0
                    ? "Expires today"
                    : `${contract.expiry_days} days remaining`}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ContractRegister({ contracts, filters, onFilters, onOpen }) {
  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            data-testid="amc-contract-search"
            onChange={(event) =>
              onFilters({ ...filters, q: event.target.value })
            }
            placeholder="Search number, title, provider"
            value={filters.q}
          />
        </div>
        <select
          className="border bg-background px-3 py-2 text-sm"
          data-testid="amc-contract-status-filter"
          onChange={(event) =>
            onFilters({ ...filters, status: event.target.value })
          }
          value={filters.status}
        >
          <option value="">All lifecycle states</option>
          {[
            "DRAFT",
            "ACTIVE",
            "EXPIRING_SOON",
            "EXPIRED",
            "RENEWED",
            "TERMINATED",
            "CANCELLED",
          ].map((item) => (
            <option key={item} value={item}>
              {item.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </div>
      <div
        className="overflow-x-auto border bg-card"
        data-testid="amc-contract-table"
      >
        <table className="min-w-[1050px] w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-3">Contract</th>
              <th className="p-3">Provider</th>
              <th className="p-3">Coverage</th>
              <th className="p-3">Period</th>
              <th className="p-3">Value</th>
              <th className="p-3">Lifecycle</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0
              ? tableEmpty(
                  "No contracts match these filters.",
                  "amc-contract-empty",
                  7,
                )
              : contracts.map((contract) => (
                  <tr
                    className="border-t"
                    data-testid={`amc-contract-row-${contract.contract_id}`}
                    key={contract.contract_id}
                  >
                    <td className="p-3">
                      <div className="font-medium">{contract.title}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {contract.contract_number} · {contract.contract_type}
                      </div>
                    </td>
                    <td className="p-3">{contract.provider_name}</td>
                    <td className="p-3">
                      {contract.coverage_type.replaceAll("_", " ")}
                      <div className="text-xs text-muted-foreground">
                        {contract.assets?.length || 0} assets ·{" "}
                        {contract.covered_locations?.length || 0} locations
                      </div>
                    </td>
                    <td className="p-3">
                      {contract.start_date}
                      <div className="text-xs text-muted-foreground">
                        to {contract.end_date}
                      </div>
                    </td>
                    <td className="p-3">
                      {formatMoney(contract.total_contract_value)}
                    </td>
                    <td className="p-3">
                      <StateBadge value={contract.derived_status} />
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        data-testid={`amc-contract-open-${contract.contract_id}`}
                        onClick={() => onOpen(contract.contract_id)}
                        size="sm"
                        variant="outline"
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScheduleRegister({ schedules, onOpen }) {
  return (
    <div
      className="overflow-x-auto border bg-card"
      data-testid="amc-schedule-table"
    >
      <table className="min-w-[900px] w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
          <tr>
            <th className="p-3">Planned</th>
            <th className="p-3">Contract</th>
            <th className="p-3">Service</th>
            <th className="p-3">Coverage</th>
            <th className="p-3">State</th>
            <th className="p-3" />
          </tr>
        </thead>
        <tbody>
          {schedules.length === 0
            ? tableEmpty("No service schedules yet.", "amc-schedule-empty", 6)
            : schedules.map((schedule) => (
                <tr
                  className="border-t"
                  data-testid={`amc-schedule-row-${schedule.schedule_id}`}
                  key={schedule.schedule_id}
                >
                  <td className="p-3">{schedule.planned_date}</td>
                  <td className="p-3 font-mono text-xs">
                    {schedule.contract_number}
                  </td>
                  <td className="p-3">{schedule.service_type}</td>
                  <td className="p-3">
                    {schedule.assets
                      ?.map((item) => item.asset_name)
                      .join(", ") ||
                      schedule.covered_locations
                        ?.map((item) => item.name)
                        .join(", ") ||
                      "Whole Society"}
                  </td>
                  <td className="p-3">
                    <StateBadge value={schedule.derived_status} />
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`amc-schedule-contract-${schedule.schedule_id}`}
                      onClick={() => onOpen(schedule.contract_id)}
                      size="sm"
                      variant="outline"
                    >
                      Contract
                    </Button>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}

function VisitRegister({ visits }) {
  return (
    <div
      className="overflow-x-auto border bg-card"
      data-testid="amc-visit-table"
    >
      <table className="min-w-[900px] w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
          <tr>
            <th className="p-3">Actual visit</th>
            <th className="p-3">Contract</th>
            <th className="p-3">Provider / technician</th>
            <th className="p-3">Scheduled date</th>
            <th className="p-3">Work performed</th>
          </tr>
        </thead>
        <tbody>
          {visits.length === 0
            ? tableEmpty(
                "No completed service visits yet.",
                "amc-visit-empty",
                5,
              )
            : visits.map((visit) => (
                <tr
                  className="border-t"
                  data-testid={`amc-visit-row-${visit.visit_id}`}
                  key={visit.visit_id}
                >
                  <td className="p-3">{visit.visit_date}</td>
                  <td className="p-3 font-mono text-xs">
                    {visit.contract_number}
                  </td>
                  <td className="p-3">
                    {visit.provider_name}
                    <div className="text-xs text-muted-foreground">
                      {visit.technician_name || "Technician not recorded"}
                    </div>
                  </td>
                  <td className="p-3">
                    {visit.scheduled_date || "Ad-hoc visit"}
                  </td>
                  <td className="p-3">
                    {visit.work_performed || "No work notes"}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}

function ProviderRegister({ bootstrap, providers, onChanged }) {
  const [form, setForm] = useState({
    name: "",
    contact_person: "",
    mobile: "",
    email: "",
    platform_vendor_id: "",
  });
  const create = async () => {
    try {
      await api.post("/society-amc/providers", form);
      toast.success("Service provider saved");
      setForm({
        name: "",
        contact_person: "",
        mobile: "",
        email: "",
        platform_vendor_id: "",
      });
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save provider");
    }
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
      <section className="border bg-card p-5" data-testid="amc-provider-form">
        <h2 className="font-display font-bold">Add service provider</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Use an existing platform Vendor or keep a Society-local record.
        </p>
        <div className="mt-4 grid gap-3">
          <Field label="Platform Vendor (optional)">
            <Select
              onValueChange={(value) => {
                const vendor = bootstrap.platform_vendors.find(
                  (item) => item.vendor_id === value,
                );
                setForm({
                  ...form,
                  platform_vendor_id: value,
                  name: vendor?.business_name || form.name,
                  contact_person: vendor?.contact_person || form.contact_person,
                  mobile: vendor?.mobile || form.mobile,
                  email: vendor?.email || form.email,
                });
              }}
              value={form.platform_vendor_id}
            >
              <SelectTrigger data-testid="amc-provider-platform-vendor">
                <SelectValue placeholder="No platform vendor" />
              </SelectTrigger>
              <SelectContent>
                {bootstrap.platform_vendors.map((vendor) => (
                  <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>
                    {vendor.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Provider name *">
            <Input
              data-testid="amc-provider-name"
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
              value={form.name}
            />
          </Field>
          <Field label="Contact person">
            <Input
              data-testid="amc-provider-contact"
              onChange={(event) =>
                setForm({ ...form, contact_person: event.target.value })
              }
              value={form.contact_person}
            />
          </Field>
          <Field label="Mobile">
            <Input
              data-testid="amc-provider-mobile"
              onChange={(event) =>
                setForm({ ...form, mobile: event.target.value })
              }
              value={form.mobile}
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="amc-provider-email"
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
              value={form.email}
            />
          </Field>
          <Button data-testid="amc-provider-save" onClick={create}>
            <Plus className="mr-1 h-4 w-4" />
            Save Provider
          </Button>
        </div>
      </section>
      <section
        className="overflow-x-auto border bg-card"
        data-testid="amc-provider-table"
      >
        <table className="min-w-[700px] w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-3">Provider</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Source</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {providers.length === 0
              ? tableEmpty(
                  "No service providers have been saved.",
                  "amc-provider-empty",
                  4,
                )
              : providers.map((provider) => (
                  <tr
                    className="border-t"
                    data-testid={`amc-provider-row-${provider.provider_id}`}
                    key={provider.provider_id}
                  >
                    <td className="p-3 font-medium">{provider.name}</td>
                    <td className="p-3">
                      {provider.contact_person || "—"}
                      <div className="text-xs text-muted-foreground">
                        {provider.mobile || provider.email || "No contact"}
                      </div>
                    </td>
                    <td className="p-3">
                      {provider.source === "PLATFORM_VENDOR"
                        ? "Platform Vendor"
                        : "Society Local"}
                    </td>
                    <td className="p-3">{provider.status}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ReportsPanel() {
  const reports = [
    ["contract-register", "Contract Register"],
    ["active-contracts", "Active Contracts"],
    ["expired-contracts", "Expired Contracts"],
    ["expiring-contracts", "Expiring Contracts"],
    ["renewal-due", "Renewal Due"],
    ["contract-value", "Contract Value Register"],
    ["provider-contracts", "Provider-wise Contracts"],
    ["asset-amc", "Asset-wise AMC"],
    ["service-schedule", "Service Schedule"],
    ["upcoming-services", "Upcoming Services"],
    ["overdue-services", "Overdue Services"],
    ["completed-visits", "Completed Service Visits"],
    ["missed-services", "Missed Service Visits"],
    ["renewal-history", "Renewal History"],
  ];
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
      data-testid="amc-reports-grid"
    >
      {reports.map(([slug, label]) => (
        <div
          className="border bg-card p-4"
          data-testid={`amc-report-${slug}`}
          key={slug}
        >
          <div className="font-display font-bold">{label}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              data-testid={`amc-report-json-${slug}`}
              href={`${API}/society-amc/reports/${slug}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button size="sm" variant="outline">
                View
              </Button>
            </a>
            <a
              data-testid={`amc-report-csv-${slug}`}
              href={`${API}/society-amc/reports/${slug}?format=csv`}
            >
              <Button size="sm" variant="outline">
                <FileDown className="mr-1 h-4 w-4" />
                CSV
              </Button>
            </a>
            <a
              data-testid={`amc-report-xlsx-${slug}`}
              href={`${API}/society-amc/reports/${slug}?format=xlsx`}
            >
              <Button size="sm" variant="outline">
                XLSX
              </Button>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsPanel({ bootstrap, onChanged }) {
  const [type, setType] = useState("");
  const [area, setArea] = useState("");
  const save = async (path, value, message) => {
    try {
      await api.post(path, { name: value });
      toast.success(message);
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save setting");
    }
  };
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <section
        className="border bg-card p-5"
        data-testid="amc-contract-type-settings"
      >
        <h2 className="font-display font-bold">Contract types</h2>
        <div className="mt-4 flex gap-2">
          <Input
            data-testid="amc-contract-type-input"
            onChange={(event) => setType(event.target.value)}
            placeholder="Custom contract type"
            value={type}
          />
          <Button
            data-testid="amc-contract-type-save"
            onClick={() => {
              save("/society-amc/contract-types", type, "Contract type added");
              setType("");
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {bootstrap.contract_types.map((item) => (
            <span
              className="border px-2 py-1 text-xs"
              data-testid={`amc-contract-type-${item.contract_type_id}`}
              key={item.contract_type_id}
            >
              {item.name}
            </span>
          ))}
        </div>
      </section>
      <section
        className="border bg-card p-5"
        data-testid="amc-common-area-settings"
      >
        <h2 className="font-display font-bold">Common areas</h2>
        <div className="mt-4 flex gap-2">
          <Input
            data-testid="amc-common-area-input"
            onChange={(event) => setArea(event.target.value)}
            placeholder="Garden, terrace, clubhouse"
            value={area}
          />
          <Button
            data-testid="amc-common-area-save"
            onClick={() => {
              save("/society-amc/locations", area, "Common area added");
              setArea("");
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {bootstrap.common_areas.map((item) => (
            <span
              className="border px-2 py-1 text-xs"
              data-testid={`amc-common-area-${item.location_id}`}
              key={item.location_id}
            >
              {item.name}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}

function ContractEditor({ bootstrap, onChanged, onOpenChange, open }) {
  const [form, setForm] = useState(emptyContract);
  const update = (key, value) => setForm({ ...form, [key]: value });
  const save = async () => {
    try {
      await api.post("/society-amc/contracts", {
        ...form,
        contract_value: Number(form.contract_value || 0),
        tax_amount: Number(form.tax_amount || 0),
        planned_visits: Number(form.planned_visits || 0),
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Contract created as a draft");
      setForm(emptyContract);
      onOpenChange(false);
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create contract");
    }
  };
  const locations = [
    ...bootstrap.buildings.map((item) => ({
      location_type: "BUILDING",
      reference_id: item.building_id,
      name: item.name,
    })),
    ...bootstrap.wings.map((item) => ({
      location_type: "WING",
      reference_id: item.wing_id,
      name: item.name,
    })),
    ...bootstrap.common_areas.map((item) => ({
      location_type: "COMMON_AREA",
      reference_id: item.location_id,
      name: item.name,
    })),
  ];
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className="max-h-[92vh] max-w-5xl overflow-y-auto"
        data-testid="amc-contract-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            New AMC / Service Contract
          </DialogTitle>
          <DialogDescription>
            Capture the provider commitment, coverage, schedule, and renewal
            details.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Contract title *">
            <Input
              data-testid="amc-contract-title"
              onChange={(event) => update("title", event.target.value)}
              value={form.title}
            />
          </Field>
          <Field label="Contract type *">
            <Select
              onValueChange={(value) => update("contract_type", value)}
              value={form.contract_type}
            >
              <SelectTrigger data-testid="amc-contract-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {bootstrap.contract_types
                  .filter((item) => item.active)
                  .map((item) => (
                    <SelectItem key={item.contract_type_id} value={item.name}>
                      {item.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Provider *">
            <Select
              onValueChange={(value) => update("provider_id", value)}
              value={form.provider_id}
            >
              <SelectTrigger data-testid="amc-contract-provider">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {bootstrap.providers.map((item) => (
                  <SelectItem key={item.provider_id} value={item.provider_id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <DatePicker
            label="Start date *"
            onChange={(value) => update("start_date", value)}
            testid="amc-contract-start"
            value={form.start_date}
          />
          <DatePicker
            label="End date *"
            onChange={(value) => update("end_date", value)}
            testid="amc-contract-end"
            value={form.end_date}
          />
          <Field label="Coverage type">
            <Select
              onValueChange={(value) => update("coverage_type", value)}
              value={form.coverage_type}
            >
              <SelectTrigger data-testid="amc-contract-coverage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[
                  "ASSET",
                  "MULTIPLE_ASSETS",
                  "SERVICE",
                  "LOCATION",
                  "WHOLE_SOCIETY",
                  "HYBRID",
                ].map((item) => (
                  <SelectItem key={item} value={item}>
                    {item.replaceAll("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Contract value">
            <Input
              data-testid="amc-contract-value"
              min="0"
              onChange={(event) => update("contract_value", event.target.value)}
              type="number"
              value={form.contract_value}
            />
          </Field>
          <Field label="Tax amount">
            <Input
              data-testid="amc-contract-tax"
              min="0"
              onChange={(event) => update("tax_amount", event.target.value)}
              type="number"
              value={form.tax_amount}
            />
          </Field>
          <Field label="Planned visits">
            <Input
              data-testid="amc-contract-planned-visits"
              min="0"
              onChange={(event) => update("planned_visits", event.target.value)}
              type="number"
              value={form.planned_visits}
            />
          </Field>
          <Field label="Service frequency">
            <Select
              onValueChange={(value) => update("service_frequency", value)}
              value={form.service_frequency}
            >
              <SelectTrigger data-testid="amc-contract-service-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {frequencyOptions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item.replaceAll("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Billing frequency">
            <Select
              onValueChange={(value) => update("billing_frequency", value)}
              value={form.billing_frequency}
            >
              <SelectTrigger data-testid="amc-contract-billing-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {frequencyOptions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item.replaceAll("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Renewal notice days">
            <Input
              data-testid="amc-contract-renewal-notice"
              min="0"
              onChange={(event) =>
                update("renewal_notice_days", event.target.value)
              }
              type="number"
              value={form.renewal_notice_days}
            />
          </Field>
          <Field label="Covered service">
            <Input
              data-testid="amc-contract-covered-service"
              onChange={(event) =>
                update("covered_service", event.target.value)
              }
              placeholder="Optional service coverage"
              value={form.covered_service}
            />
          </Field>
          <DatePicker
            label="Next service date"
            onChange={(value) => update("next_service_date", value)}
            testid="amc-contract-next-service"
            value={form.next_service_date}
          />
          <Field label="Payment terms">
            <Input
              data-testid="amc-contract-payment-terms"
              onChange={(event) => update("payment_terms", event.target.value)}
              value={form.payment_terms}
            />
          </Field>
          <div className="md:col-span-3">
            <Field label="Covered assets">
              <div
                className={
                  "grid max-h-36 grid-cols-1 gap-2 overflow-y-auto border p-3 " +
                  "sm:grid-cols-2"
                }
                data-testid="amc-contract-assets"
              >
                {bootstrap.assets.map((asset) => (
                  <label
                    className="flex items-center gap-2 text-sm"
                    key={asset.asset_id}
                  >
                    <input
                      checked={form.asset_ids.includes(asset.asset_id)}
                      data-testid={`amc-contract-asset-${asset.asset_id}`}
                      onChange={(event) =>
                        update(
                          "asset_ids",
                          event.target.checked
                            ? [...form.asset_ids, asset.asset_id]
                            : form.asset_ids.filter(
                                (id) => id !== asset.asset_id,
                              ),
                        )
                      }
                      type="checkbox"
                    />
                    {asset.asset_code} · {asset.name}
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Covered locations">
              <div
                className={
                  "grid max-h-36 grid-cols-1 gap-2 overflow-y-auto border p-3 " +
                  "sm:grid-cols-2"
                }
                data-testid="amc-contract-locations"
              >
                {locations.map((location) => (
                  <label
                    className="flex items-center gap-2 text-sm"
                    key={`${location.location_type}-${location.reference_id}`}
                  >
                    <input
                      checked={form.covered_locations.some(
                        (item) => item.reference_id === location.reference_id,
                      )}
                      data-testid={`amc-contract-location-${location.reference_id}`}
                      onChange={(event) =>
                        update(
                          "covered_locations",
                          event.target.checked
                            ? [...form.covered_locations, location]
                            : form.covered_locations.filter(
                                (item) =>
                                  item.reference_id !== location.reference_id,
                              ),
                        )
                      }
                      type="checkbox"
                    />
                    {location.name}
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Terms & conditions">
              <Textarea
                data-testid="amc-contract-terms"
                onChange={(event) =>
                  update("terms_conditions", event.target.value)
                }
                value={form.terms_conditions}
              />
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Remarks">
              <Textarea
                data-testid="amc-contract-remarks"
                onChange={(event) => update("remarks", event.target.value)}
                value={form.remarks}
              />
            </Field>
          </div>
        </div>
        <Button data-testid="amc-contract-save" onClick={save}>
          Create Draft Contract
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function Contract360({ bootstrap, contractId, onChanged, onClose }) {
  const [data, setData] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [visit, setVisit] = useState({
    visit_date: "",
    work_performed: "",
    technician_name: "",
    schedule_id: "",
    ticket_id: "",
  });
  const [renewal, setRenewal] = useState({
    start_date: "",
    end_date: "",
    contract_value: "",
    tax_amount: "",
    renewal_notes: "",
  });
  const [docType, setDocType] = useState("SIGNED_CONTRACT");
  const documentInput = useRef(null);
  const load = async () => {
    if (!contractId) return;
    try {
      const response = await api.get(`/society-amc/contracts/${contractId}`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Contract unavailable");
      onClose();
    }
  };
  useEffect(() => {
    setData(null);
    load();
  }, [contractId]);
  const refresh = () => {
    load();
    onChanged();
  };
  const action = async (path, body, success) => {
    try {
      await api.post(path, body);
      toast.success(success);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not complete action");
    }
  };
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(
        `/society-amc/contracts/${contractId}/documents?document_type=${docType}`,
        form,
      );
      toast.success("Contract document uploaded");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not upload document");
    } finally {
      event.target.value = "";
    }
  };
  if (!contractId) return null;
  return (
    <Dialog
      onOpenChange={(open) => !open && onClose()}
      open={Boolean(contractId)}
    >
      <DialogContent
        className="max-h-[94vh] max-w-6xl overflow-y-auto"
        data-testid="amc-contract-360-dialog"
      >
        {!data ? (
          <Loading />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                {data.contract.contract_number} · {data.contract.title}
              </DialogTitle>
              <DialogDescription>
                Review coverage, service evidence, renewal history, and the
                audit timeline.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="border p-3">
                <div className="text-xs text-muted-foreground">Lifecycle</div>
                <StateBadge value={data.contract.derived_status} />
              </div>
              <div className="border p-3">
                <div className="text-xs text-muted-foreground">Provider</div>
                <div className="font-medium">{data.contract.provider_name}</div>
              </div>
              <div className="border p-3">
                <div className="text-xs text-muted-foreground">
                  Contract period
                </div>
                <div className="font-medium">
                  {data.contract.start_date} → {data.contract.end_date}
                </div>
              </div>
              <div className="border p-3">
                <div className="text-xs text-muted-foreground">Total value</div>
                <div className="font-medium">
                  {formatMoney(data.contract.total_contract_value)}
                </div>
              </div>
            </div>
            <section
              className="mt-4 flex flex-wrap gap-2"
              data-testid="amc-contract-actions"
            >
              {data.contract.derived_status === "DRAFT" && (
                <Button
                  data-testid="amc-contract-activate"
                  onClick={() =>
                    action(
                      `/society-amc/contracts/${contractId}/activate`,
                      {},
                      "Contract activated",
                    )
                  }
                >
                  Activate
                </Button>
              )}
              <Button
                data-testid="amc-contract-renew"
                onClick={() =>
                  document
                    .getElementById("amc-renewal-panel")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                variant="outline"
              >
                <History className="mr-1 h-4 w-4" />
                Renew Contract
              </Button>
              <Button
                data-testid="amc-contract-cancel"
                onClick={() => {
                  const reason = window.prompt("Cancellation reason");
                  if (reason)
                    action(
                      `/society-amc/contracts/${contractId}/lifecycle`,
                      { status: "CANCELLED", reason },
                      "Contract cancelled",
                    );
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                data-testid="amc-contract-terminate"
                onClick={() => {
                  const reason = window.prompt("Termination reason");
                  if (reason)
                    action(
                      `/society-amc/contracts/${contractId}/lifecycle`,
                      { status: "TERMINATED", reason },
                      "Contract terminated",
                    );
                }}
                variant="outline"
              >
                Terminate
              </Button>
            </section>
            <section className="mt-5 grid gap-5 xl:grid-cols-2">
              <div className="border p-4" data-testid="amc-contract-coverage">
                <h3 className="font-display font-bold">Coverage</h3>
                <div className="mt-3 text-sm">
                  <b>{data.contract.coverage_type.replaceAll("_", " ")}</b>
                  <div className="mt-2">
                    Assets:{" "}
                    {data.contract.assets
                      ?.map((asset) => asset.asset_code || asset.asset_name)
                      .join(", ") || "None"}
                  </div>
                  <div className="mt-1">
                    Locations:{" "}
                    {data.contract.covered_locations
                      ?.map((location) => location.name)
                      .join(", ") || "None"}
                  </div>
                  <div className="mt-1">
                    Service: {data.contract.covered_service || "Not specified"}
                  </div>
                </div>
              </div>
              <div
                className="border p-4"
                data-testid="amc-contract-renewal-history"
              >
                <h3 className="font-display font-bold">Renewal history</h3>
                {data.renewal_history.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    No renewal chain yet.
                  </p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {data.renewal_history.map((item) => (
                      <div
                        className="border-l-2 border-primary/30 pl-3 text-sm"
                        key={item.contract_id}
                      >
                        <b>{item.contract_number}</b> · {item.start_date} to{" "}
                        {item.end_date}
                        <div>
                          <StateBadge value={item.derived_status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
            <section
              className="mt-5 border p-4"
              data-testid="amc-contract-documents"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 font-display font-bold">
                  <FileText className="h-4 w-4" />
                  Contract documents
                </h3>
                <div className="flex gap-2">
                  <Select onValueChange={setDocType} value={docType}>
                    <SelectTrigger
                      data-testid="amc-document-type"
                      className="w-52"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {bootstrap.document_types.map((item) => (
                        <SelectItem key={item} value={item}>
                          {item.replaceAll("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    data-testid={`amc-document-input-${contractId}`}
                    hidden
                    onChange={upload}
                    ref={documentInput}
                    type="file"
                  />
                  <Button
                    data-testid={`amc-document-upload-${contractId}`}
                    onClick={() => documentInput.current?.click()}
                    size="sm"
                    variant="outline"
                  >
                    Upload
                  </Button>
                </div>
              </div>
              {data.documents.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No contract documents yet.
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {data.documents.map((item) => (
                    <a
                      className="block text-sm text-primary underline"
                      data-testid={`amc-document-${item.contract_document_id}`}
                      href={`${API}/society-amc/documents/${item.contract_document_id}/download`}
                      key={item.contract_document_id}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.document_type.replaceAll("_", " ")} ·{" "}
                      {item.filename}
                    </a>
                  ))}
                </div>
              )}
            </section>
            <section className="mt-5 grid gap-5 xl:grid-cols-2">
              <div className="border p-4" data-testid="amc-contract-schedules">
                <h3 className="font-display font-bold">Service schedule</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  <DatePicker
                    label="Planned date"
                    onChange={setScheduleDate}
                    testid="amc-schedule-date"
                    value={scheduleDate}
                  />
                  <Button
                    data-testid="amc-schedule-create"
                    onClick={() =>
                      action(
                        `/society-amc/contracts/${contractId}/schedules`,
                        {
                          planned_date: scheduleDate,
                          idempotency_key: crypto.randomUUID(),
                        },
                        "Service scheduled",
                      )
                    }
                    variant="outline"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Schedule
                  </Button>
                </div>
                <div className="mt-4 space-y-2">
                  {data.schedules.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No service obligations scheduled.
                    </p>
                  ) : (
                    data.schedules.map((item) => (
                      <div
                        className="border p-2 text-sm"
                        data-testid={`amc-contract-schedule-${item.schedule_id}`}
                        key={item.schedule_id}
                      >
                        <b>{item.planned_date}</b> · {item.service_type}
                        <div className="mt-1">
                          <StateBadge value={item.derived_status} />
                        </div>
                        {item.derived_status !== "COMPLETED" && (
                          <Button
                            className="mt-2"
                            data-testid={`amc-schedule-reschedule-${item.schedule_id}`}
                            onClick={() => {
                              const date = window.prompt("New YYYY-MM-DD date");
                              const reason = window.prompt("Reason");
                              if (date && reason)
                                action(
                                  `/society-amc/schedules/${item.schedule_id}/reschedule`,
                                  { planned_date: date, reason },
                                  "Service rescheduled",
                                );
                            }}
                            size="sm"
                            variant="ghost"
                          >
                            Reschedule
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="border p-4" data-testid="amc-contract-visits">
                <h3 className="font-display font-bold">
                  Complete service visit
                </h3>
                <div className="mt-3 grid gap-2">
                  <DatePicker
                    label="Actual visit date"
                    onChange={(value) =>
                      setVisit({ ...visit, visit_date: value })
                    }
                    testid="amc-visit-date"
                    value={visit.visit_date}
                  />
                  <Input
                    data-testid="amc-visit-technician"
                    onChange={(event) =>
                      setVisit({
                        ...visit,
                        technician_name: event.target.value,
                      })
                    }
                    placeholder="Technician name"
                    value={visit.technician_name}
                  />
                  <Textarea
                    data-testid="amc-visit-work"
                    onChange={(event) =>
                      setVisit({ ...visit, work_performed: event.target.value })
                    }
                    placeholder="Work performed"
                    value={visit.work_performed}
                  />
                  <Select
                    onValueChange={(value) =>
                      setVisit({ ...visit, schedule_id: value })
                    }
                    value={visit.schedule_id}
                  >
                    <SelectTrigger data-testid="amc-visit-schedule">
                      <SelectValue placeholder="Scheduled visit (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {data.schedules
                        .filter((item) => item.derived_status !== "COMPLETED")
                        .map((item) => (
                          <SelectItem
                            key={item.schedule_id}
                            value={item.schedule_id}
                          >
                            {item.planned_date} · {item.service_type}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="amc-visit-complete"
                    onClick={() =>
                      action(
                        `/society-amc/contracts/${contractId}/visits`,
                        { ...visit, idempotency_key: crypto.randomUUID() },
                        "Service visit completed",
                      )
                    }
                  >
                    Complete Visit
                  </Button>
                </div>
                <div className="mt-4 space-y-2">
                  {data.visits.map((item) => (
                    <div
                      className="border-l-2 border-secondary/40 pl-3 text-sm"
                      data-testid={`amc-contract-visit-${item.visit_id}`}
                      key={item.visit_id}
                    >
                      <b>{item.visit_date}</b>
                      {item.scheduled_date && (
                        <span className="text-muted-foreground">
                          {" "}
                          · planned {item.scheduled_date}
                        </span>
                      )}
                      <div>{item.work_performed || "No work notes"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            <section
              className="mt-5 border p-4"
              id="amc-renewal-panel"
              data-testid="amc-contract-renewal-panel"
            >
              <h3 className="font-display font-bold">
                Renew contract as a new historical record
              </h3>
              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <DatePicker
                  label="New start date"
                  onChange={(value) =>
                    setRenewal({ ...renewal, start_date: value })
                  }
                  testid="amc-renew-start"
                  value={renewal.start_date}
                />
                <DatePicker
                  label="New end date"
                  onChange={(value) =>
                    setRenewal({ ...renewal, end_date: value })
                  }
                  testid="amc-renew-end"
                  value={renewal.end_date}
                />
                <Field label="New value">
                  <Input
                    data-testid="amc-renew-value"
                    onChange={(event) =>
                      setRenewal({
                        ...renewal,
                        contract_value: event.target.value,
                      })
                    }
                    type="number"
                    value={renewal.contract_value}
                  />
                </Field>
                <Field label="Tax">
                  <Input
                    data-testid="amc-renew-tax"
                    onChange={(event) =>
                      setRenewal({ ...renewal, tax_amount: event.target.value })
                    }
                    type="number"
                    value={renewal.tax_amount}
                  />
                </Field>
              </div>
              <Textarea
                className="mt-3"
                data-testid="amc-renew-notes"
                onChange={(event) =>
                  setRenewal({ ...renewal, renewal_notes: event.target.value })
                }
                placeholder="Renewal notes"
                value={renewal.renewal_notes}
              />
              <Button
                className="mt-3"
                data-testid="amc-renew-submit"
                onClick={() =>
                  action(
                    `/society-amc/contracts/${contractId}/renew`,
                    {
                      ...renewal,
                      contract_value: Number(renewal.contract_value || 0),
                      tax_amount: Number(renewal.tax_amount || 0),
                      idempotency_key: crypto.randomUUID(),
                    },
                    "Renewal contract created as draft",
                  )
                }
              >
                Create Renewal Contract
              </Button>
            </section>
            <section className="mt-5 grid gap-5 xl:grid-cols-2">
              <div className="border p-4" data-testid="amc-contract-helpdesk">
                <h3 className="font-display font-bold">
                  Related Helpdesk Tickets
                </h3>
                {data.tickets.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    No Helpdesk ticket is linked to this contract.
                  </p>
                ) : (
                  data.tickets.map((item) => (
                    <div
                      className="mt-2 border p-2 text-sm"
                      key={item.ticket_id}
                    >
                      {item.ticket_number} · {item.subject}
                    </div>
                  ))
                )}
              </div>
              <div className="border p-4" data-testid="amc-contract-audit">
                <h3 className="font-display font-bold">Audit timeline</h3>
                {data.audit_timeline.slice(0, 8).map((item) => (
                  <div
                    className="mt-2 border-l-2 border-primary/30 pl-3 text-sm"
                    key={item.log_id}
                  >
                    <b>{item.action}</b> · {item.timestamp}
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
